import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { getWorkspaceFromUrl } from "@/hooks/use-workspace";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Loader2, Users, ShieldAlert, Trash2, Key, UserPlus, Mail, 
  Shield, ChevronRight, Check, X, Minus, Search, Phone, Building, Clock,
  UserCheck, ArrowUp, Globe, Layers, LogIn, Pencil
} from "lucide-react";
import { triggerViewAs } from "@/lib/impersonation";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { UserProfile } from "@shared/schema";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSource, getAvatarInitials } from "@/lib/avatar";

interface UserWithProfile {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
  profilePicture: string | null;
  isActive?: boolean | null;
  deactivatedAt?: Date | string | null;
  createdAt: Date | null;
  profile?: {
    id: string;
    userId: string;
    role: string;
    departmentId?: string | null;
    reportsToUserId?: string | null;
    isManager?: boolean | null;
    allowedRegionIds?: string[] | null;
    allowedClientTypeIds?: string[] | null;
  };
}

const DEPARTMENT_OPTIONS = [
  { value: "sales", label: "Sales" },
  { value: "marketing", label: "Marketing" },
  { value: "talent", label: "Talent" },
  { value: "billing", label: "Billing" },
  { value: "ops", label: "Ops" },
];

interface AccessRequest {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  message?: string;
  status: "pending" | "approved" | "denied";
  assignedRole?: string;
  processedAt?: string;
  createdAt: string;
}

interface PermissionDefinition {
  id: string;
  key: string;
  module: string;
  type: string;
  label: string;
  description: string | null;
  sortOrder: number;
}

interface WorkspaceUser {
  userId: string;
  workspaceId: string;
  role: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
}

interface UserPermissions {
  userId: string;
  workspaceId: string;
  role: string;
  rolePermissions: Record<string, boolean>;
  userOverrides: Record<string, boolean>;
  // Task #576 — only present when the requester is a super admin. Lets
  // the UI surface platform-level overrides set by the platform owner
  // (separate from the workspace-admin `userOverrides` above) and show
  // which one is in effect for the workspace currently being viewed.
  platformOverrides?: {
    global: Record<string, boolean>;
    workspaceScoped: Record<string, boolean>;
    effectiveSource: Record<string, "global" | "workspace_scoped">;
  };
}

interface RolePermissions {
  role: string;
  permissions: Record<string, boolean>;
}

const ROLE_OPTIONS = [
  { value: "sales_rep", label: "Sales Rep" },
  { value: "billing", label: "Billing" },
  { value: "artist", label: "Talent" },
  // v2: { value: "marketing", label: "Marketing" },
  { value: "admin", label: "Admin" },
];

const ROLE_LABELS: Record<string, string> = {
  all: "All Users",
  admin: "Admin",
  sales_rep: "Sales Rep",
  artist: "Talent",
  billing: "Billing",
  marketing: "Marketing",
};

const MODULE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  leads: "Leads",
  clients: "Clients",
  invoices: "Invoices",
  schedule: "Schedule",
  approvals: "Approvals",
  communications: "Communications",
  reports: "Reports",
  forms: "Documents",
  admin: "Admin Panel",
  platform: "Platform",
  myProfile: "Talent Profile",
  myEvents: "Talent Events",
};

function orderPermissionModules<T>(entries: [string, T][]): [string, T][] {
  const rest = entries.filter(([m]) => m !== "myProfile" && m !== "myEvents");
  const talentSiblings = entries.filter(([m]) => m === "myProfile" || m === "myEvents");
  talentSiblings.sort(([a], [b]) => {
    const order = ["myProfile", "myEvents"];
    return order.indexOf(a) - order.indexOf(b);
  });
  const talentIdx = rest.findIndex(([m]) => m === "talent");
  if (talentIdx >= 0) {
    rest.splice(talentIdx + 1, 0, ...talentSiblings);
  } else {
    rest.push(...talentSiblings);
  }
  return rest;
}

const HIDDEN_MODULES_FROM_PERMISSIONS = ["billing", "marketing", "knowledge", "socialMedia"];

const PLATFORM_HIDDEN_ROLES = ["admin", "sales_rep", "artist", "billing", "all"];

// Task #576 — small inline indicator placed next to each permission row in
// the "By User" tab. Shows whether a super-admin has set a global and/or
// workspace-scoped *platform* override for this (user, permission), and
// which one is in effect for the workspace currently being viewed. Only
// renders anything when the API actually returned `platformOverrides`
// (i.e. the requester is a super admin); workspace admins see nothing.
function PlatformOverrideBadges({
  permissionKey,
  platformOverrides,
}: {
  permissionKey: string;
  platformOverrides?: UserPermissions["platformOverrides"];
}) {
  if (!platformOverrides) return null;
  const hasGlobal = permissionKey in platformOverrides.global;
  const hasScoped = permissionKey in platformOverrides.workspaceScoped;
  if (!hasGlobal && !hasScoped) return null;

  const winner = platformOverrides.effectiveSource[permissionKey];
  const bothExist = hasGlobal && hasScoped;
  const globalAllowed = platformOverrides.global[permissionKey];
  const scopedAllowed = platformOverrides.workspaceScoped[permissionKey];

  const renderBadge = (
    kind: "global" | "workspace_scoped",
    allowed: boolean,
    isWinner: boolean,
  ) => {
    const Icon = kind === "global" ? Globe : Layers;
    const label = kind === "global" ? "Platform global" : "Platform workspace";
    const allowText = allowed ? "Allow" : "Deny";
    const tip = bothExist
      ? `${label}: ${allowText}${isWinner ? " — in effect for this workspace" : " — overridden by the workspace-scoped platform override"}`
      : `${label} override: ${allowText}`;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`gap-1 px-1.5 py-0 text-[10px] h-5 ${
              isWinner
                ? allowed
                  ? "border-green-500 text-green-700 dark:text-green-400 bg-green-500/10"
                  : "border-red-500 text-red-700 dark:text-red-400 bg-red-500/10"
                : "opacity-60"
            }`}
            data-testid={`badge-platform-${kind}-${permissionKey}`}
            data-in-effect={isWinner ? "true" : "false"}
          >
            <Icon className="w-3 h-3" />
            {allowed ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            {isWinner && bothExist && <span className="ml-0.5">★</span>}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top">{tip}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="flex items-center gap-1"
        data-testid={`platform-overrides-${permissionKey}`}
        data-has-global={hasGlobal ? "true" : "false"}
        data-has-scoped={hasScoped ? "true" : "false"}
        data-effective-source={winner || ""}
      >
        {hasGlobal && renderBadge("global", globalAllowed, winner === "global")}
        {hasScoped && renderBadge("workspace_scoped", scopedAllowed, winner === "workspace_scoped")}
      </div>
    </TooltipProvider>
  );
}

// Task #576 — small banner above the per-permission rows that surfaces a
// count of (user, permission) pairs that have BOTH a global and a
// workspace-scoped platform override. Lets a super admin spot at a glance
// that there's a precedence collision somewhere on this user without
// having to scroll the full permission list. Renders nothing when the
// API didn't return platform overrides (i.e. workspace admins).
function PlatformOverridesSummary({
  platformOverrides,
}: {
  platformOverrides?: UserPermissions["platformOverrides"];
}) {
  if (!platformOverrides) return null;
  const globalKeys = new Set(Object.keys(platformOverrides.global));
  const scopedKeys = new Set(Object.keys(platformOverrides.workspaceScoped));
  const totalKeys = new Set([...globalKeys, ...scopedKeys]);
  if (totalKeys.size === 0) return null;
  const collisions: string[] = [];
  for (const key of totalKeys) {
    if (globalKeys.has(key) && scopedKeys.has(key)) collisions.push(key);
  }
  return (
    <div
      className="mb-3 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700/50 px-3 py-2 text-xs flex items-start gap-2"
      data-testid="banner-platform-overrides-summary"
      data-collision-count={collisions.length}
      data-total-count={totalKeys.size}
    >
      <ShieldAlert className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
      <div className="space-y-0.5">
        <div className="font-medium text-amber-900 dark:text-amber-200">
          Platform-level overrides on this user
        </div>
        <div className="text-amber-800/90 dark:text-amber-300/90">
          {totalKeys.size} permission{totalKeys.size === 1 ? "" : "s"} have a platform-set override.
          {collisions.length > 0 && (
            <>
              {" "}
              <span className="font-medium">{collisions.length}</span> ha{collisions.length === 1 ? "s" : "ve"} both a{" "}
              <span className="inline-flex items-center gap-1 align-middle">
                <Globe className="w-3 h-3" /> global
              </span>{" "}
              and a{" "}
              <span className="inline-flex items-center gap-1 align-middle">
                <Layers className="w-3 h-3" /> workspace-scoped
              </span>{" "}
              override — the workspace-scoped one wins for this workspace (marked ★ on the row).
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface UsersAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: "roles" | "permissions" | "requests";
  inPanel?: boolean;
}

export function UsersAccessDialog({ open, onOpenChange, defaultTab = "permissions", inPanel = false }: UsersAccessDialogProps) {
  const [activeTab, setActiveTab] = useState(defaultTab === "requests" ? "permissions" : defaultTab);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setActiveTab(defaultTab === "requests" ? "permissions" : defaultTab);
    }
    onOpenChange(newOpen);
  };

  const panelContent = (
    <>
      {/* Header is hidden when rendered as an admin-panel section because the
          admin page already shows the section title + description at the top.
          Keeping it inside the modal variant so the dialog still self-labels. */}
      {!inPanel && (
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Users & Permissions
          </DialogTitle>
          <DialogDescription>
            Manage user roles, permissions, and access requests
          </DialogDescription>
        </DialogHeader>
      )}

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="permissions" className="gap-2" data-testid="tab-permissions">
            <Shield className="w-4 h-4" />
            Permissions
          </TabsTrigger>
          <TabsTrigger value="roles" className="gap-2" data-testid="tab-roles">
            <Users className="w-4 h-4" />
            Users
          </TabsTrigger>
          {false && (
          <TabsTrigger value="requests" className="gap-2" data-testid="tab-access-requests">
            <UserCheck className="w-4 h-4" />
            Access Requests
          </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="permissions" className="flex-1 overflow-auto mt-4">
          <PermissionsTabContent open={open || inPanel} />
        </TabsContent>

        <TabsContent value="roles" className="flex-1 overflow-auto mt-4">
          <RolesTabContent open={open || inPanel} />
        </TabsContent>

        {false && (
        <TabsContent value="requests" className="flex-1 overflow-auto mt-4">
          <AccessRequestsTabContent open={open || inPanel} />
        </TabsContent>
        )}
      </Tabs>
    </>
  );

  if (inPanel) {
    return (
      <div className="w-full h-full flex flex-col overflow-hidden p-4 sm:p-6">
        {panelContent}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {panelContent}
      </DialogContent>
    </Dialog>
  );
}

function RegionAccessCell({ user }: { user: UserWithProfile }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const { data: regions = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["/api/regions"],
  });
  const allowed: string[] = user.profile?.allowedRegionIds || [];
  const role = user.profile?.role;
  const bypass = role === "admin" || role === "billing";

  const mutation = useMutation({
    mutationFn: async (next: string[]) => {
      const response = await apiRequest("PATCH", `/api/admin/users/${user.id}/region-access`, { allowedRegionIds: next });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Saved", description: "Region access updated." });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Failed to update access.", variant: "destructive" });
    },
  });

  const toggle = (id: string) => {
    const next = allowed.includes(id) ? allowed.filter(x => x !== id) : [...allowed, id];
    mutation.mutate(next);
  };

  if (bypass) {
    return <Badge variant="outline" className="text-xs" title="Admin/Billing roles see all regions">Sees all (role bypass)</Badge>;
  }

  const summary = allowed.length === 0
    ? <Badge variant="secondary" className="text-xs">All regions</Badge>
    : (
      <div className="flex flex-wrap gap-1 max-w-[260px]">
        {allowed.slice(0, 3).map(id => {
          const r = regions.find(rr => rr.id === id);
          return <Badge key={id} variant="outline" className="text-xs">{r?.name || id}</Badge>;
        })}
        {allowed.length > 3 && <Badge variant="outline" className="text-xs">+{allowed.length - 3}</Badge>}
      </div>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-1 -ml-1 hover-elevate"
          data-testid={`button-region-access-${user.id}`}
          disabled={mutation.isPending}
        >
          {summary}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <div className="space-y-2">
          <div className="text-sm font-medium">Visible Regions</div>
          <p className="text-xs text-muted-foreground">
            Empty = sees all. Select specific regions to restrict this user's leads, clients, invoices, and schedule view.
          </p>
          {allowed.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => mutation.mutate([])}
              data-testid={`button-region-clear-${user.id}`}
            >
              Clear (allow all)
            </Button>
          )}
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {regions.map(r => {
              const checked = allowed.includes(r.id);
              return (
                <label
                  key={r.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover-elevate cursor-pointer"
                  data-testid={`row-region-access-${user.id}-${r.id}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(r.id)}
                    disabled={mutation.isPending}
                  />
                  <span className="text-sm">{r.name}</span>
                </label>
              );
            })}
            {regions.length === 0 && (
              <p className="text-xs text-muted-foreground px-2">No regions configured yet.</p>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ClientTypeAccessCell({ user }: { user: UserWithProfile }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const { data: clientTypes = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["/api/client-types"],
  });
  const allowed: string[] = user.profile?.allowedClientTypeIds || [];
  const role = user.profile?.role;
  const bypass = role === "admin" || role === "billing";

  const mutation = useMutation({
    mutationFn: async (next: string[]) => {
      const response = await apiRequest("PATCH", `/api/admin/users/${user.id}/client-type-access`, { allowedClientTypeIds: next });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Saved", description: "Client type access updated." });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Failed to update access.", variant: "destructive" });
    },
  });

  const toggle = (id: string) => {
    const next = allowed.includes(id) ? allowed.filter(x => x !== id) : [...allowed, id];
    mutation.mutate(next);
  };

  if (bypass) {
    return <Badge variant="outline" className="text-xs" title="Admin/Billing roles see all client types">Sees all (role bypass)</Badge>;
  }

  const summary = allowed.length === 0
    ? <Badge variant="secondary" className="text-xs">All client types</Badge>
    : (
      <div className="flex flex-wrap gap-1 max-w-[260px]">
        {allowed.slice(0, 3).map(id => {
          const ct = clientTypes.find(c => c.id === id);
          return <Badge key={id} variant="outline" className="text-xs">{ct?.name || id}</Badge>;
        })}
        {allowed.length > 3 && <Badge variant="outline" className="text-xs">+{allowed.length - 3}</Badge>}
      </div>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-1 -ml-1 hover-elevate"
          data-testid={`button-client-type-access-${user.id}`}
          disabled={mutation.isPending}
        >
          {summary}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <div className="space-y-2">
          <div className="text-sm font-medium">Visible Client Types</div>
          <p className="text-xs text-muted-foreground">
            Empty = sees all. Select specific types to restrict this user's leads, clients, invoices, and schedule view.
          </p>
          {allowed.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => mutation.mutate([])}
              data-testid={`button-client-type-clear-${user.id}`}
            >
              Clear (allow all)
            </Button>
          )}
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {clientTypes.map(ct => {
              const checked = allowed.includes(ct.id);
              return (
                <label
                  key={ct.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover-elevate cursor-pointer"
                  data-testid={`row-client-type-access-${user.id}-${ct.id}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(ct.id)}
                    disabled={mutation.isPending}
                  />
                  <span className="text-sm">{ct.name}</span>
                </label>
              );
            })}
            {clientTypes.length === 0 && (
              <p className="text-xs text-muted-foreground px-2">No client types configured yet.</p>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Task #702 — Manager Role Foundation. Per-user editor that lets an
// admin set the user's department, mark them as a manager, and pick
// their direct manager (reportsToUserId). Cycle prevention is enforced
// by the backend; the picker only filters out the user themself client-
// side. The full list comes from /api/admin/users which the parent
// already loads.
function ManagerSettingsCell({
  user,
  allUsers,
}: {
  user: UserWithProfile;
  allUsers: UserWithProfile[];
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const profile = user.profile;
  const departmentId = profile?.departmentId || "ops";
  const reportsToUserId = profile?.reportsToUserId || null;
  const isManager = !!profile?.isManager;

  // Task #702 — client-side cycle precheck. The backend is still the
  // source of truth (assertNoReportsToCycle in server/lib/manager-visibility.ts),
  // but we also walk the proposed chain in-memory so the user gets an
  // immediate error toast instead of a round-trip 400. Returns null on
  // OK, or a human-readable error message on detected cycle.
  function detectCycleClientSide(proposedManagerId: string | null): string | null {
    if (!proposedManagerId) return null;
    if (proposedManagerId === user.id) {
      return "A user can't report to themselves.";
    }
    // Walk UP from the proposed manager looking for `user.id`.
    const byId = new Map(allUsers.map((u) => [u.id, u]));
    const seen = new Set<string>();
    let cursor: string | null = proposedManagerId;
    while (cursor) {
      if (cursor === user.id) {
        return "That assignment would create a reporting loop.";
      }
      if (seen.has(cursor)) {
        // A pre-existing cycle in the data — refuse to extend it.
        return "Existing reporting chain has a loop; please fix it first.";
      }
      seen.add(cursor);
      const node = byId.get(cursor);
      cursor = node?.profile?.reportsToUserId || null;
    }
    return null;
  }

  const mutation = useMutation({
    mutationFn: async (next: {
      departmentId?: string;
      reportsToUserId?: string | null;
      isManager?: boolean;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/admin/users/${user.id}/manager-settings`,
        next,
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspace/org-chart"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspace/manager-setup-needed"] });
      toast({ title: "Saved", description: "Manager settings updated." });
    },
    onError: (e: any) => {
      toast({
        title: "Error",
        description: e?.message || "Failed to update manager settings.",
        variant: "destructive",
      });
    },
  });

  const managerName = (() => {
    if (!reportsToUserId) return null;
    const m = allUsers.find((u) => u.id === reportsToUserId);
    if (!m) return reportsToUserId;
    return [m.firstName, m.lastName].filter(Boolean).join(" ").trim() || m.email || m.id;
  })();

  const departmentLabel =
    DEPARTMENT_OPTIONS.find((d) => d.value === departmentId)?.label || "Ops";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-1 -ml-1 hover-elevate"
          data-testid={`button-manager-settings-${user.id}`}
          disabled={mutation.isPending}
        >
          <div className="flex flex-col items-start gap-1 max-w-[180px]">
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="text-xs">
                {departmentLabel}
              </Badge>
              {isManager && (
                <Badge variant="secondary" className="text-xs" title="Manager">
                  Mgr
                </Badge>
              )}
            </div>
            <span className="text-xs text-muted-foreground truncate max-w-[170px]">
              {managerName ? `↑ ${managerName}` : "No manager"}
            </span>
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium">Department</div>
            <Select
              value={departmentId}
              onValueChange={(value) => mutation.mutate({ departmentId: value })}
              disabled={mutation.isPending}
            >
              <SelectTrigger
                className="h-8 mt-1"
                data-testid={`select-department-${user.id}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENT_OPTIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="text-sm font-medium">Reports to</div>
            <Select
              value={reportsToUserId || "__none__"}
              onValueChange={(value) => {
                const next = value === "__none__" ? null : value;
                const cycleError = detectCycleClientSide(next);
                if (cycleError) {
                  toast({
                    title: "Can't set manager",
                    description: cycleError,
                    variant: "destructive",
                  });
                  return;
                }
                mutation.mutate({ reportsToUserId: next });
              }}
              disabled={mutation.isPending}
            >
              <SelectTrigger
                className="h-8 mt-1"
                data-testid={`select-reports-to-${user.id}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No manager</SelectItem>
                {allUsers
                  .filter((u) => u.id !== user.id)
                  .map((u) => {
                    const name =
                      [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
                      u.email ||
                      u.id;
                    return (
                      <SelectItem key={u.id} value={u.id}>
                        {name}
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Cycles are blocked automatically.
            </p>
          </div>

          <label className="flex items-center gap-2 px-1 py-1.5 rounded hover-elevate cursor-pointer">
            <Checkbox
              checked={isManager}
              onCheckedChange={(v) =>
                mutation.mutate({ isManager: v === true })
              }
              disabled={mutation.isPending}
              data-testid={`checkbox-is-manager-${user.id}`}
            />
            <span className="text-sm">
              Is a manager (sees their direct + indirect reports' records)
            </span>
          </label>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RolesTabContent({ open }: { open: boolean }) {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [userToDelete, setUserToDelete] = useState<UserWithProfile | null>(null);
  const [userToResetPassword, setUserToResetPassword] = useState<UserWithProfile | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [userToEdit, setUserToEdit] = useState<UserWithProfile | null>(null);
  const [editUserData, setEditUserData] = useState({ firstName: "", lastName: "", email: "" });
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "sales_rep",
    password: "",
  });
  // When the entered email already has an account (in this or another
  // workspace), the server responds with a confirm prompt instead of creating
  // a duplicate. This holds that prompt so we can ask before attaching them.
  const [pendingConfirmUser, setPendingConfirmUser] = useState<
    {
      email: string;
      name: string | null;
      message?: string;
      // The exact payload that triggered the prompt, frozen so the confirm
      // replays it verbatim even if the form behind the dialog changes.
      payload: typeof newUserData & { confirmAddExisting?: boolean };
    } | null
  >(null);

  const { data: users = [], isLoading } = useQuery<UserWithProfile[]>({
    // Pass includeInactive=true so admins can see and re-activate
    // deactivated users. Every other consumer of /api/admin/users
    // gets active-only by default.
    queryKey: ["/api/admin/users", { includeInactive: true }],
    queryFn: async () => {
      const res = await fetch("/api/admin/users?includeInactive=true", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch users");
      return res.json();
    },
    enabled: open,
  });

  const setActiveMutation = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      const response = await apiRequest("PATCH", `/api/admin/users/${userId}/active`, { isActive });
      return response.json();
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/users-with-profiles"] });
      toast({
        title: vars.isActive ? "User Reactivated" : "User Deactivated",
        description: vars.isActive
          ? "They can sign in again and will reappear in selectors."
          : "They can no longer sign in and are hidden from selectors. Historic records remain.",
      });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update user status.", variant: "destructive" });
    },
  });

  // Task #702 — surface a one-time setup banner when this workspace
  // still looks like the back-fill default (everyone in Ops, no managers,
  // no reporting lines). Admins can dismiss it locally; the server only
  // tells us "needed: true/false" — the dismiss state lives in
  // localStorage so it stays per-admin.
  const { data: setupStatus } = useQuery<{ needed: boolean; memberCount?: number }>({
    queryKey: ["/api/workspace/manager-setup-needed"],
    enabled: open,
  });
  const dismissKey = "manager-setup-banner-dismissed";
  const [setupDismissed, setSetupDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(dismissKey) === "1";
  });
  // v2: Manager Panel hidden — suppress the org-chart setup banner until re-enabled
  const showSetupBanner = false && !!setupStatus?.needed && !setupDismissed;

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const response = await apiRequest("PATCH", `/api/admin/users/${userId}/role`, { role });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Role Updated", description: "User role has been updated successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update user role.", variant: "destructive" });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiRequest("DELETE", `/api/admin/users/${userId}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setUserToDelete(null);
      toast({ title: "User Deleted", description: "User has been deleted successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete user.", variant: "destructive" });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, newPassword }: { userId: string; newPassword: string }) => {
      const response = await apiRequest("POST", `/api/admin/users/${userId}/reset-password`, { newPassword });
      return response.json();
    },
    onSuccess: () => {
      setUserToResetPassword(null);
      setNewPassword("");
      toast({ title: "Password Reset", description: "User's password has been reset successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to reset password.", variant: "destructive" });
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async (userData: typeof newUserData & { confirmAddExisting?: boolean }) => {
      try {
        const response = await apiRequest("POST", "/api/admin/users", userData);
        return await response.json();
      } catch (err: any) {
        // The server returns 409 + USER_EXISTS_CONFIRM when the email already
        // has an account that isn't in this workspace. Surface it as a confirm
        // prompt instead of an error toast. apiRequest throws "409: <body>".
        const msg = String(err?.message || "");
        if (msg.startsWith("409: ")) {
          try {
            const parsed = JSON.parse(msg.slice(5));
            if (parsed?.code === "USER_EXISTS_CONFIRM") {
              return {
                __needsConfirm: true,
                existingEmail: parsed.existingEmail,
                existingName: parsed.existingName ?? null,
                message: parsed.message,
              };
            }
          } catch { /* not our JSON — rethrow below */ }
        }
        throw err;
      }
    },
    onSuccess: (data: any, variables) => {
      if (data?.__needsConfirm) {
        setPendingConfirmUser({
          email: data.existingEmail,
          name: data.existingName,
          message: data.message,
          payload: variables,
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setShowAddUser(false);
      setPendingConfirmUser(null);
      setNewUserData({ firstName: "", lastName: "", email: "", role: "sales_rep", password: "" });
      toast({ title: "User Added", description: data?.message || "User has been added successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create user.", variant: "destructive" });
    },
  });

  const editUserMutation = useMutation({
    mutationFn: async ({ userId, ...data }: { userId: string; firstName: string; lastName: string; email: string }) => {
      const response = await apiRequest("PATCH", `/api/admin/users/${userId}/profile`, data);
      if (!response.ok) {
        const body = await response.json();
        throw new Error(body.message || "Failed to update user");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setUserToEdit(null);
      toast({ title: "User Updated", description: "Name and email updated successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update user.", variant: "destructive" });
    },
  });

  // Legacy `isProtectedUser` (email allowlist) removed — superadmin
  // gating now lives server-side in isSuperAdminEmail. UI just blocks
  // self-modification.
  const isCurrentUser = (user: UserWithProfile) => currentUser?.id === user.id;
  const canDeleteUser = (user: UserWithProfile) => !isCurrentUser(user);
  const canChangeRole = (user: UserWithProfile) => !isCurrentUser(user);
  const isUserActive = (user: UserWithProfile) => user.isActive !== false;
  const canToggleActive = (user: UserWithProfile) => !isCurrentUser(user);

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "admin": return "destructive";
      case "sales_rep": return "default";
      case "artist": return "secondary";
      case "billing": return "outline";
      case "marketing": return "secondary";
      default: return "outline";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showSetupBanner && (
        <div
          className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 flex items-start gap-3"
          data-testid="banner-manager-setup"
        >
          <UserCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-medium">Set up your org chart</div>
            <p className="text-muted-foreground mt-1">
              Everyone is currently set to <strong>Ops</strong> with no manager. Assign
              departments and reporting lines below so managers can see their reports'
              leads, clients, and invoices.
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              window.localStorage.setItem(dismissKey, "1");
              setSetupDismissed(true);
            }}
            data-testid="button-dismiss-manager-setup"
          >
            Dismiss
          </Button>
        </div>
      )}
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{users.length} users total</p>
        <Button onClick={() => setShowAddUser(true)} data-testid="button-add-user">
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {showAddUser && (
        <div className="border rounded-lg p-4 bg-muted/30 space-y-4">
          <h3 className="font-medium flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Add New User
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={newUserData.firstName}
                onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                placeholder="John"
                data-testid="input-new-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={newUserData.lastName}
                onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                placeholder="Doe"
                data-testid="input-new-last-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                placeholder="john@example.com"
                data-testid="input-new-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                placeholder="Enter password"
                data-testid="input-new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={newUserData.role}
                onValueChange={(value) => setNewUserData({ ...newUserData, role: value })}
              >
                <SelectTrigger data-testid="select-new-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowAddUser(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createUserMutation.mutate(newUserData)}
              disabled={!newUserData.firstName || !newUserData.email || !newUserData.password || createUserMutation.isPending}
              data-testid="button-create-user"
            >
              {createUserMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create User
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              {/* v2: <TableHead>Department / Manager</TableHead> */}
              <TableHead>Visible Client Types</TableHead>
              <TableHead>Visible Regions</TableHead>
              <TableHead className="w-[150px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="w-8 h-8" data-testid={`avatar-user-${user.id}`}>
                      <AvatarImage
                        src={getAvatarSource({
                          profilePicture: user.profilePicture,
                          profileImageUrl: user.profileImageUrl,
                        })}
                        alt={
                          user.firstName || user.lastName
                            ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                            : user.email || "User"
                        }
                      />
                      <AvatarFallback className="text-sm font-medium">
                        {getAvatarInitials(
                          user.firstName || user.lastName
                            ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                            : user.email,
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">
                        {user.firstName || user.lastName ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "—"}
                      </div>
                      {isCurrentUser(user) && (
                        <Badge variant="secondary" className="text-xs mt-1">You</Badge>
                      )}
                      {!isUserActive(user) && (
                        <Badge
                          variant="outline"
                          className="text-xs mt-1 border-amber-500/40 text-amber-600 dark:text-amber-400"
                          data-testid={`badge-inactive-${user.id}`}
                        >
                          Inactive
                        </Badge>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Mail className="w-3 h-3 text-muted-foreground" />
                    <span className="text-sm">{user.email || "—"}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {canChangeRole(user) ? (
                    <Select
                      value={user.profile?.role || "sales_rep"}
                      onValueChange={(value) => updateRoleMutation.mutate({ userId: user.id, role: value })}
                    >
                      <SelectTrigger className="w-[130px]" data-testid={`select-role-${user.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant={getRoleBadgeVariant(user.profile?.role || "")}>
                      {ROLE_OPTIONS.find((r) => r.value === user.profile?.role)?.label || user.profile?.role || "—"}
                    </Badge>
                  )}
                </TableCell>
                {/* v2: <TableCell><ManagerSettingsCell user={user} allUsers={users} /></TableCell> */}
                <TableCell>
                  <ClientTypeAccessCell user={user} />
                </TableCell>
                <TableCell>
                  <RegionAccessCell user={user} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {!isCurrentUser(user) && isUserActive(user) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => triggerViewAs(user.id)}
                        title="Log in as this user"
                        data-testid={`button-login-as-${user.id}`}
                      >
                        <LogIn className="w-4 h-4" />
                      </Button>
                    )}
                    {!isCurrentUser(user) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditUserData({
                            firstName: user.firstName || "",
                            lastName: user.lastName || "",
                            email: user.email || "",
                          });
                          setUserToEdit(user);
                        }}
                        title="Edit name / email"
                        data-testid={`button-edit-user-${user.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setUserToResetPassword(user)}
                      title="Reset Password"
                      data-testid={`button-reset-password-${user.id}`}
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    {canToggleActive(user) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          setActiveMutation.mutate({
                            userId: user.id,
                            isActive: !isUserActive(user),
                          })
                        }
                        disabled={setActiveMutation.isPending}
                        title={isUserActive(user) ? "Deactivate user" : "Reactivate user"}
                        data-testid={`button-toggle-active-${user.id}`}
                      >
                        {isUserActive(user) ? (
                          <X className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        ) : (
                          <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        )}
                      </Button>
                    )}
                    {canDeleteUser(user) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setUserToDelete(user)}
                        title="Delete User"
                        data-testid={`button-delete-user-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!userToEdit} onOpenChange={(open) => { if (!open) setUserToEdit(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update the name or email for {userToEdit?.firstName} {userToEdit?.lastName}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-firstName">First Name</Label>
                <Input
                  id="edit-firstName"
                  value={editUserData.firstName}
                  onChange={(e) => setEditUserData({ ...editUserData, firstName: e.target.value })}
                  placeholder="First name"
                  data-testid="input-edit-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-lastName">Last Name</Label>
                <Input
                  id="edit-lastName"
                  value={editUserData.lastName}
                  onChange={(e) => setEditUserData({ ...editUserData, lastName: e.target.value })}
                  placeholder="Last name"
                  data-testid="input-edit-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editUserData.email}
                onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                placeholder="Email address"
                data-testid="input-edit-email"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setUserToEdit(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                userToEdit &&
                editUserMutation.mutate({
                  userId: userToEdit.id,
                  ...editUserData,
                })
              }
              disabled={
                !editUserData.firstName ||
                !editUserData.lastName ||
                !editUserData.email ||
                editUserMutation.isPending
              }
              data-testid="button-save-edit-user"
            >
              {editUserMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingConfirmUser} onOpenChange={(o) => { if (!o) setPendingConfirmUser(null); }}>
        <AlertDialogContent data-testid="dialog-confirm-add-existing">
          <AlertDialogHeader>
            <AlertDialogTitle>Account already exists</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingConfirmUser?.message
                || `An account for ${pendingConfirmUser?.email ?? "this email"} already exists. Add them to this workspace?`}
              {" "}They'll get access to this workspace only.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-add-existing">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-add-existing"
              disabled={createUserMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!pendingConfirmUser) return;
                createUserMutation.mutate({ ...pendingConfirmUser.payload, confirmAddExisting: true });
              }}
            >
              {createUserMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add to workspace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {userToDelete?.firstName} {userToDelete?.lastName}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => userToDelete && deleteUserMutation.mutate(userToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!userToResetPassword} onOpenChange={() => { setUserToResetPassword(null); setNewPassword(""); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password</AlertDialogTitle>
            <AlertDialogDescription>
              Set a new password for {userToResetPassword?.firstName} {userToResetPassword?.lastName}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              data-testid="input-new-password-reset"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => userToResetPassword && resetPasswordMutation.mutate({ userId: userToResetPassword.id, newPassword })}
              disabled={!newPassword || resetPasswordMutation.isPending}
            >
              Reset Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface TierVisibility {
  tier: string;
  displayName: string;
  visibility: Record<string, "allow" | "deny" | "hide">;
}

function PermissionsTabContent({ open }: { open: boolean }) {
  const { toast } = useToast();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>("admin");
  const [userSearch, setUserSearch] = useState("");
  const [permissionTab, setPermissionTab] = useState<"by-user" | "by-role">("by-role");

  const { data: definitions = [], isLoading: defsLoading } = useQuery<PermissionDefinition[]>({
    queryKey: ["/api/admin/permissions/definitions"],
    enabled: open,
  });

  // Fetch tier visibility settings to hide permissions at tier level
  const workspaceSlug = getWorkspaceFromUrl();
  const { data: tierVisibility, isLoading: tierVisibilityLoading, isError: tierVisibilityError } = useQuery<TierVisibility>({
    queryKey: ["/api/tier-visibility", workspaceSlug],
    queryFn: async () => {
      // Need to get workspace ID from slug first
      const wsRes = await fetch(`/api/workspaces/by-slug/${workspaceSlug}`);
      if (!wsRes.ok) throw new Error("Failed to get workspace");
      const ws = await wsRes.json();
      const res = await fetch(`/api/w/${ws.id}/tier-visibility`);
      if (!res.ok) throw new Error("Failed to get tier visibility");
      return res.json();
    },
    enabled: open && !!workspaceSlug,
    retry: 2,
  });

  // Filter definitions to exclude hidden permissions based on tier
  // If tier visibility fails to load, show all permissions (graceful degradation)
  const visibleDefinitions = useMemo(() => {
    // If still loading tier visibility, return empty to prevent flicker
    if (tierVisibilityLoading) return [];
    // If error or no visibility data, show all permissions (graceful degradation)
    if (!tierVisibility?.visibility || tierVisibilityError) return definitions;
    // Filter out permissions marked as hidden by the tier
    return definitions.filter(def => tierVisibility.visibility[def.key] !== "hide");
  }, [definitions, tierVisibility, tierVisibilityLoading, tierVisibilityError]);

  const { data: users = [], isLoading: usersLoading } = useQuery<WorkspaceUser[]>({
    queryKey: ["/api/admin/permissions/users"],
    enabled: open,
  });

  const { data: userPermissions, isLoading: userPermsLoading } = useQuery<UserPermissions>({
    queryKey: ["/api/admin/permissions/users", selectedUserId],
    enabled: open && !!selectedUserId,
  });

  const { data: rolePermissions, isLoading: rolePermsLoading } = useQuery<RolePermissions>({
    queryKey: ["/api/admin/permissions/roles", selectedRole],
    enabled: open,
  });

  // Always fetch "All Users" baseline permissions to show inheritance for other roles
  const { data: allUsersBaseline } = useQuery<RolePermissions>({
    queryKey: ["/api/admin/permissions/roles", "all"],
    enabled: open && selectedRole !== "all",
  });

  const setOverrideMutation = useMutation({
    mutationFn: async ({ userId, permissionKey, allowed }: { userId: string; permissionKey: string; allowed: boolean }) => {
      const response = await apiRequest("POST", `/api/admin/permissions/users/${userId}/override`, { permissionKey, allowed });
      return response.json();
    },
    onSuccess: () => {
      // Only invalidate the specific user's permissions, not global effective permissions
      // to prevent parent re-renders that could close this dialog
      queryClient.invalidateQueries({ queryKey: ["/api/admin/permissions/users", selectedUserId] });
      toast({ title: "Permission Updated", description: "User permission override has been set." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update permission.", variant: "destructive" });
    },
  });

  const removeOverrideMutation = useMutation({
    mutationFn: async ({ userId, permissionKey }: { userId: string; permissionKey: string }) => {
      const response = await apiRequest("DELETE", `/api/admin/permissions/users/${userId}/override/${permissionKey}`);
      return response.json();
    },
    onSuccess: () => {
      // Only invalidate the specific user's permissions, not global effective permissions
      queryClient.invalidateQueries({ queryKey: ["/api/admin/permissions/users", selectedUserId] });
      toast({ title: "Permission Reset", description: "User permission has been reset to role default." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to reset permission.", variant: "destructive" });
    },
  });

  const updateRolePermMutation = useMutation({
    mutationFn: async ({ role, permissionKey, allowed }: { role: string; permissionKey: string; allowed: boolean }) => {
      const response = await apiRequest("POST", `/api/admin/permissions/roles/${role}`, { permissionKey, allowed });
      return response.json();
    },
    onSuccess: () => {
      // Only invalidate role permissions, not global effective permissions
      queryClient.invalidateQueries({ queryKey: ["/api/admin/permissions/roles", selectedRole] });
      toast({ title: "Role Permission Updated", description: "Role default permission has been updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update role permission.", variant: "destructive" });
    },
  });

  const deleteRolePermMutation = useMutation({
    mutationFn: async ({ role, permissionKey }: { role: string; permissionKey: string }) => {
      const response = await apiRequest("DELETE", `/api/admin/permissions/roles/${role}/${permissionKey}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/permissions/roles", selectedRole] });
      toast({ title: "Permission Reset", description: selectedRole === "all" ? "Permission set to Variable (each role determines own value)." : "Role permission has been removed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to reset permission.", variant: "destructive" });
    },
  });

  const filteredUsers = users.filter(u =>
    u.fullName.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Group visible definitions (after filtering hidden permissions based on tier)
  const groupedDefinitions = visibleDefinitions.reduce((acc, def) => {
    if (!acc[def.module]) acc[def.module] = [];
    acc[def.module].push(def);
    return acc;
  }, {} as Record<string, PermissionDefinition[]>);

  const getPermissionState = (permissionKey: string): "allow" | "deny" | "inherit" => {
    if (!userPermissions) return "inherit";
    if (permissionKey in userPermissions.userOverrides) {
      return userPermissions.userOverrides[permissionKey] ? "allow" : "deny";
    }
    return "inherit";
  };

  const getEffectiveValue = (permissionKey: string): boolean => {
    if (!userPermissions) return false;
    if (permissionKey in userPermissions.userOverrides) {
      return userPermissions.userOverrides[permissionKey];
    }
    return userPermissions.rolePermissions[permissionKey] ?? false;
  };

  if (defsLoading || usersLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs value={permissionTab} onValueChange={(v) => setPermissionTab(v as typeof permissionTab)}>
        <TabsList>
          <TabsTrigger value="by-role">By Role</TabsTrigger>
          <TabsTrigger value="by-user">By User</TabsTrigger>
        </TabsList>

        <TabsContent value="by-user" className="mt-4">
          <div className="flex gap-4">
            <div className="w-64 space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-8"
                  data-testid="input-search-users"
                />
              </div>
              <ScrollArea className="h-[calc(100vh-22rem)] border rounded-md">
                <div className="p-2 space-y-1">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.userId}
                      onClick={() => setSelectedUserId(user.userId)}
                      className={`w-full text-left p-2 rounded-md hover-elevate ${selectedUserId === user.userId ? "bg-primary/10" : ""}`}
                      data-testid={`button-select-user-${user.userId}`}
                    >
                      <div className="font-medium text-sm">{user.fullName}</div>
                      <div className="text-xs text-muted-foreground">{user.email}</div>
                      <Badge variant="outline" className="text-xs mt-1">
                        {ROLE_LABELS[user.role] || user.role}
                      </Badge>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            <div className="flex-1">
              {!selectedUserId ? (
                <div className="flex items-center justify-center h-[500px] text-muted-foreground">
                  Select a user to manage their permissions
                </div>
              ) : userPermsLoading ? (
                <div className="flex items-center justify-center h-[calc(100vh-22rem)]">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ScrollArea className="h-[calc(100vh-22rem)]">
                  <PlatformOverridesSummary platformOverrides={userPermissions?.platformOverrides} />
                  <div className="space-y-4">
                    {orderPermissionModules(Object.entries(groupedDefinitions))
                      .filter(([module]) => !HIDDEN_MODULES_FROM_PERMISSIONS.includes(module))
                      .filter(([module]) => module !== "platform" || !PLATFORM_HIDDEN_ROLES.includes(userPermissions?.role || ""))
                      .map(([module, perms]) => (
                        <div key={module} className="space-y-2">
                          <h4 className="font-medium text-sm flex items-center gap-2">
                            <ChevronRight className="w-4 h-4" />
                            {MODULE_LABELS[module] || module}
                          </h4>
                          <div className="space-y-1 ml-6">
                            {perms.map((perm) => {
                              const state = getPermissionState(perm.key);
                              const effective = getEffectiveValue(perm.key);
                              return (
                                <div key={perm.key} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                                  <div>
                                    <div className="text-sm">{perm.label}</div>
                                    {perm.description && (
                                      <div className="text-xs text-muted-foreground">{perm.description}</div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <PlatformOverrideBadges
                                      permissionKey={perm.key}
                                      platformOverrides={userPermissions?.platformOverrides}
                                    />
                                    <div className={`w-2 h-2 rounded-full ${effective ? "bg-green-500" : "bg-red-500"}`} />
                                    <Select
                                      value={state}
                                      onValueChange={(value) => {
                                        if (value === "inherit") {
                                          removeOverrideMutation.mutate({ userId: selectedUserId!, permissionKey: perm.key });
                                        } else {
                                          setOverrideMutation.mutate({ userId: selectedUserId!, permissionKey: perm.key, allowed: value === "allow" });
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="w-[100px] h-8" data-testid={`select-perm-${perm.key}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="inherit">
                                          <span className="flex items-center gap-1">
                                            <Minus className="w-3 h-3" /> Inherit
                                          </span>
                                        </SelectItem>
                                        <SelectItem value="allow">
                                          <span className="flex items-center gap-1">
                                            <Check className="w-3 h-3 text-green-600" /> Allow
                                          </span>
                                        </SelectItem>
                                        <SelectItem value="deny">
                                          <span className="flex items-center gap-1">
                                            <X className="w-3 h-3 text-red-600" /> Deny
                                          </span>
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="by-role" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Label>Role:</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="w-[180px]" data-testid="select-role-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {rolePermsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div>
                <div className="space-y-4">
                  {orderPermissionModules(Object.entries(groupedDefinitions))
                    .filter(([module]) => !HIDDEN_MODULES_FROM_PERMISSIONS.includes(module))
                    .filter(([module]) => module !== "platform" || !PLATFORM_HIDDEN_ROLES.includes(selectedRole))
                    .map(([module, perms]) => {
                      // Calculate module status
                      const isAllUsers = selectedRole === "all";
                      const permStates = perms.map(perm => {
                        const hasValue = rolePermissions?.permissions && perm.key in rolePermissions.permissions;
                        const roleValue = rolePermissions?.permissions[perm.key];
                        // For non-"All Users" roles, inherit from baseline if no explicit value
                        const baselineValue = allUsersBaseline?.permissions?.[perm.key];
                        const effectiveAllowed = hasValue ? roleValue : (baselineValue ?? false);
                        if (isAllUsers && !hasValue) return "variable";
                        // For other roles: show "inherited" state if no explicit value but baseline exists
                        if (!isAllUsers && !hasValue && baselineValue !== undefined) return baselineValue ? "inherited-allowed" : "inherited-denied";
                        return effectiveAllowed ? "allowed" : "denied";
                      });
                      const allAllowed = permStates.every(s => s === "allowed" || s === "inherited-allowed");
                      const allDenied = permStates.every(s => s === "denied" || s === "inherited-denied");
                      const allVariable = permStates.every(s => s === "variable");
                      const allInherited = permStates.every(s => s.startsWith("inherited"));
                      const moduleStatus = allAllowed ? "allowed" : allDenied ? "denied" : allVariable ? "variable" : allInherited ? "inherited" : "mixed";
                      
                      return (
                      <div key={module} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm flex items-center gap-2">
                            <ChevronRight className="w-4 h-4" />
                            {MODULE_LABELS[module] || module}
                            <Badge 
                              variant={moduleStatus === "allowed" ? "default" : moduleStatus === "denied" ? "outline" : "secondary"}
                              className="text-xs"
                            >
                              {moduleStatus === "allowed" && <><Check className="w-3 h-3 mr-1" />All Allowed</>}
                              {moduleStatus === "denied" && <><X className="w-3 h-3 mr-1" />All Denied</>}
                              {moduleStatus === "variable" && <><Minus className="w-3 h-3 mr-1" />All Variable</>}
                              {moduleStatus === "inherited" && <><ArrowUp className="w-3 h-3 mr-1" />Inherited</>}
                              {moduleStatus === "mixed" && <>Mixed</>}
                            </Badge>
                          </h4>
                          <Select
                            value=""
                            onValueChange={(value) => {
                              if (value === "allow-all") {
                                perms.forEach(perm => {
                                  updateRolePermMutation.mutate({ role: selectedRole, permissionKey: perm.key, allowed: true });
                                });
                              } else if (value === "deny-all") {
                                perms.forEach(perm => {
                                  updateRolePermMutation.mutate({ role: selectedRole, permissionKey: perm.key, allowed: false });
                                });
                              } else if (value === "variable-all" && isAllUsers) {
                                perms.forEach(perm => {
                                  deleteRolePermMutation.mutate({ role: selectedRole, permissionKey: perm.key });
                                });
                              } else if (value === "reset-to-inherited" && !isAllUsers) {
                                perms.forEach(perm => {
                                  deleteRolePermMutation.mutate({ role: selectedRole, permissionKey: perm.key });
                                });
                              }
                            }}
                          >
                            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`dropdown-bulk-${module}`}>
                              <SelectValue placeholder="Bulk actions..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="allow-all">
                                <span className="flex items-center gap-1">
                                  <Check className="w-3 h-3 text-green-600" /> Allow All
                                </span>
                              </SelectItem>
                              <SelectItem value="deny-all">
                                <span className="flex items-center gap-1">
                                  <X className="w-3 h-3 text-red-600" /> Deny All
                                </span>
                              </SelectItem>
                              {isAllUsers && (
                                <SelectItem value="variable-all">
                                  <span className="flex items-center gap-1">
                                    <Minus className="w-3 h-3" /> Set All Variable
                                  </span>
                                </SelectItem>
                              )}
                              {!isAllUsers && (
                                <SelectItem value="reset-to-inherited">
                                  <span className="flex items-center gap-1">
                                    <ArrowUp className="w-3 h-3" /> Reset to Inherited
                                  </span>
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1 ml-6">
                          {perms.map((perm) => {
                            const hasValue = rolePermissions?.permissions && perm.key in rolePermissions.permissions;
                            const roleValue = rolePermissions?.permissions[perm.key];
                            const baselineValue = allUsersBaseline?.permissions?.[perm.key];
                            const isAllUsers = selectedRole === "all";
                            
                            // Determine effective permission (with inheritance)
                            const effectiveAllowed = hasValue ? roleValue : (baselineValue ?? false);
                            const isInherited = !isAllUsers && !hasValue && baselineValue !== undefined;
                            
                            // Tri-state for "All Users": Variable (not set) -> Allowed -> Denied -> Variable
                            // For other roles: cycle through Allowed -> Denied -> Inherited (if baseline exists)
                            const getNextState = (): "allowed" | "denied" | "variable" | "inherited" => {
                              if (isAllUsers) {
                                if (!hasValue) return "allowed"; // Variable -> Allowed
                                if (roleValue) return "denied";     // Allowed -> Denied
                                return "variable";                 // Denied -> Variable
                              }
                              // For non-All Users roles: Inherited -> Allowed -> Denied -> Inherited
                              if (isInherited) return "allowed";
                              if (roleValue) return "denied";
                              return baselineValue !== undefined ? "inherited" : "allowed";
                            };
                            
                            const handleClick = (e: React.MouseEvent) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const next = getNextState();
                              if (next === "variable" || next === "inherited") {
                                deleteRolePermMutation.mutate({ role: selectedRole, permissionKey: perm.key });
                              } else {
                                updateRolePermMutation.mutate({ role: selectedRole, permissionKey: perm.key, allowed: next === "allowed" });
                              }
                            };
                            
                            // Display state
                            const isVariable = isAllUsers && !hasValue;
                            
                            return (
                              <div key={perm.key} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                                <div>
                                  <div className="text-sm">{perm.label}</div>
                                  {perm.description && (
                                    <div className="text-xs text-muted-foreground">{perm.description}</div>
                                  )}
                                </div>
                                <Button
                                  size="sm"
                                  variant={isVariable ? "secondary" : isInherited ? "secondary" : effectiveAllowed ? "default" : "outline"}
                                  onClick={handleClick}
                                  data-testid={`button-toggle-${perm.key}`}
                                  className={isInherited ? "border-dashed" : ""}
                                >
                                  {isVariable ? (
                                    <><Minus className="w-4 h-4 mr-1" /> Variable</>
                                  ) : isInherited ? (
                                    <><ArrowUp className="w-4 h-4 mr-1" /> {baselineValue ? "Allowed" : "Denied"} (inherited)</>
                                  ) : effectiveAllowed ? (
                                    <><Check className="w-4 h-4 mr-1" /> Allowed</>
                                  ) : (
                                    <><X className="w-4 h-4 mr-1" /> Denied</>
                                  )}
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AccessRequestsTabContent({ open }: { open: boolean }) {
  const { toast } = useToast();
  const [selectedRole, setSelectedRole] = useState<Record<string, string>>({});

  const { data: requests = [], isLoading } = useQuery<AccessRequest[]>({
    queryKey: ["/api/admin/access-requests"],
    enabled: open,
  });

  const approveMutation = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) => {
      const response = await apiRequest("POST", `/api/admin/access-requests/${id}/approve`, { role });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/access-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Request Approved", description: "User has been created and notified via email." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to approve request.", variant: "destructive" });
    },
  });

  const denyMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/admin/access-requests/${id}/deny`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/access-requests"] });
      toast({ title: "Request Denied", description: "The access request has been denied." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to deny request.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/admin/access-requests/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/access-requests"] });
      toast({ title: "Request Deleted", description: "The denied access request has been removed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete request.", variant: "destructive" });
    },
  });

  const pendingRequests = requests.filter(r => r.status === "pending");
  const processedRequests = requests.filter(r => r.status !== "pending");

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">Pending</Badge>;
      case "approved":
        return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">Approved</Badge>;
      case "denied":
        return <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30">Denied</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No access requests yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {pendingRequests.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending Requests ({pendingRequests.length})
          </h3>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Assign Role</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{request.firstName} {request.lastName}</div>
                        {request.phone && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            <span>{request.phone}</span>
                          </div>
                        )}
                        {request.message && (
                          <div className="text-xs text-muted-foreground mt-1 italic max-w-[200px] truncate" title={request.message}>
                            "{request.message}"
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Mail className="w-3 h-3 text-muted-foreground" />
                        {request.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      {request.company ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Building className="w-3 h-3 text-muted-foreground" />
                          {request.company}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(request.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={selectedRole[request.id] || "sales_rep"}
                        onValueChange={(value) => setSelectedRole(prev => ({ ...prev, [request.id]: value }))}
                      >
                        <SelectTrigger className="w-[130px]" data-testid={`select-role-${request.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => approveMutation.mutate({ id: request.id, role: selectedRole[request.id] || "sales_rep" })}
                          disabled={approveMutation.isPending}
                          title="Approve"
                          data-testid={`button-approve-${request.id}`}
                        >
                          <Check className="w-4 h-4 text-green-600" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => denyMutation.mutate(request.id)}
                          disabled={denyMutation.isPending}
                          title="Deny"
                          data-testid={`button-deny-${request.id}`}
                        >
                          <X className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {processedRequests.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 text-muted-foreground">
            Previously Processed ({processedRequests.length})
          </h3>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Processed</TableHead>
                  <TableHead className="w-[60px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {processedRequests.map((request) => (
                  <TableRow key={request.id} className="opacity-70">
                    <TableCell className="font-medium">
                      {request.firstName} {request.lastName}
                    </TableCell>
                    <TableCell className="text-sm">{request.email}</TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell className="text-sm">
                      {request.assignedRole ? (
                        <Badge variant="secondary">
                          {ROLE_OPTIONS.find(r => r.value === request.assignedRole)?.label || request.assignedRole}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {request.processedAt ? formatDate(request.processedAt) : "—"}
                    </TableCell>
                    <TableCell>
                      {request.status === "denied" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => deleteMutation.mutate(request.id)}
                          disabled={deleteMutation.isPending}
                          title="Delete denied request"
                          data-testid={`button-delete-${request.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
